import {Directive, HostListener, Injectable, Injector, Input, NgModule, Optional, StaticProvider, TemplateRef, Type} from '@angular/core';
import {ComponentType} from '@angular/cdk/portal';
import {Dialog, DialogConfig, DialogModule as CdkDialogModule, DialogRef as CdkDialogRef, DIALOG_DATA} from '@angular/cdk/dialog';
import {Observable, Subject} from 'rxjs';

export const SD_DIALOG_DATA = DIALOG_DATA;

export class SdDialogConfig<D = unknown> {
  data?: D | null;
  width?: string;
  height?: string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  panelClass?: string | string[];
  backdropClass?: string | string[];
  hasBackdrop?: boolean;
  disableClose?: boolean;
  autoFocus?: boolean | string;
  restoreFocus?: boolean;
  role?: 'dialog' | 'alertdialog';
  ariaLabel?: string | null;
  ariaLabelledBy?: string | null;
  ariaDescribedBy?: string | null;
  viewContainerRef?: DialogConfig['viewContainerRef'];
  injector?: Injector;
  id?: string;
  position?: {top?: string; bottom?: string; left?: string; right?: string};
}

export class SdDialogRef<T = any, R = any> {
  private cdkRef!: CdkDialogRef<R, T>;
  private readonly closed = new Subject<R | undefined>();
  componentInstance!: T;
  disableClose: boolean | undefined;

  afterClosed(): Observable<R | undefined> {
    return this.closed.asObservable();
  }

  backdropClick(): Observable<MouseEvent> {
    return this.cdkRef?.backdropClick ?? new Subject<MouseEvent>().asObservable();
  }

  keydownEvents(): Observable<KeyboardEvent> {
    return this.cdkRef?.keydownEvents ?? new Subject<KeyboardEvent>().asObservable();
  }

  close(result?: R): void {
    this.cdkRef?.close(result);
  }

  updateSize(width?: string, height?: string): this {
    this.cdkRef?.updateSize(width, height);
    return this;
  }

  addPanelClass(classes: string | string[]): this {
    this.cdkRef?.addPanelClass(classes);
    return this;
  }

  removePanelClass(classes: string | string[]): this {
    this.cdkRef?.removePanelClass(classes);
    return this;
  }

  attach(ref: CdkDialogRef<R, T>): void {
    this.cdkRef = ref;
    this.componentInstance = ref.componentInstance as T;
    this.disableClose = ref.disableClose;
    ref.closed.subscribe((value) => {
      this.closed.next(value);
      this.closed.complete();
    });
  }
}

@Injectable({providedIn: 'root'})
export class SdDialog {
  constructor(private readonly dialog: Dialog) {}

  open<T, D = any, R = any>(
    component: ComponentType<T> | TemplateRef<T>,
    config: SdDialogConfig<D> = {},
  ): SdDialogRef<T, R> {
    const sdRef = new SdDialogRef<T, R>();
    const panelClass = [
      'sd-dialog-panel',
      ...(Array.isArray(config.panelClass) ? config.panelClass : config.panelClass ? [config.panelClass] : []),
    ];
    const providers: StaticProvider[] = [{provide: SdDialogRef, useValue: sdRef}];
    const cdkRef = this.dialog.open(component as Type<T>, {
      data: config.data,
      width: config.width || '32rem',
      height: config.height,
      minWidth: config.minWidth,
      minHeight: config.minHeight,
      maxWidth: config.maxWidth || '96vw',
      maxHeight: config.maxHeight || '90vh',
      panelClass,
      backdropClass: config.backdropClass || 'sd-dialog-backdrop',
      hasBackdrop: config.hasBackdrop !== false,
      disableClose: config.disableClose,
      autoFocus: config.autoFocus === undefined ? 'first-tabbable' : (config.autoFocus as DialogConfig['autoFocus']),
      restoreFocus: config.restoreFocus !== false,
      role: config.role,
      ariaLabel: config.ariaLabel,
      ariaLabelledBy: config.ariaLabelledBy,
      ariaDescribedBy: config.ariaDescribedBy,
      viewContainerRef: config.viewContainerRef,
      injector: config.injector,
      id: config.id,
      providers,
    });
    sdRef.attach(cdkRef as CdkDialogRef<R, T>);
    return sdRef;
  }

  closeAll(): void {
    this.dialog.closeAll();
  }
}

@Directive({
  selector: '[sd-dialog-title], [sdDialogTitle], sd-dialog-title',
  standalone: true,
  host: {class: 'sd-dialog__title sd-dialog-title block border-b border-[var(--m365-border-rest,#ececf0)] px-5 py-4 text-base font-semibold'},
})
export class SdDialogTitle {}

@Directive({
  selector: '[sd-dialog-content], [sdDialogContent], sd-dialog-content',
  standalone: true,
  host: {class: 'sd-dialog__content sd-dialog-content block max-h-[70vh] overflow-auto px-5 py-4 text-sm'},
})
export class SdDialogContent {}

@Directive({
  selector: '[sd-dialog-actions], [sdDialogActions], sd-dialog-actions',
  standalone: true,
  host: {class: 'sd-dialog__actions sd-dialog-actions flex flex-wrap items-center justify-end gap-2 border-t border-[var(--m365-border-rest,#ececf0)] px-5 py-3'},
})
export class SdDialogActions {
  @Input() align: 'start' | 'end' | 'center' | string | undefined;
}

@Directive({
  selector: '[sd-dialog-close], [sdDialogClose]',
  standalone: true,
})
export class SdDialogClose {
  @Input('sd-dialog-close') dialogResult: unknown;
  @Input() sdDialogClose: unknown;

  constructor(@Optional() private readonly dialogRef: SdDialogRef<unknown, unknown> | null) {}

  @HostListener('click')
  onClick(): void {
    const result = this.sdDialogClose !== undefined ? this.sdDialogClose : this.dialogResult;
    this.dialogRef?.close(result);
  }
}

@NgModule({
  imports: [CdkDialogModule, SdDialogTitle, SdDialogContent, SdDialogActions, SdDialogClose],
  exports: [SdDialogTitle, SdDialogContent, SdDialogActions, SdDialogClose],
  providers: [SdDialog],
})
export class SdDialogModule {}
